import React from 'react';
import DateRangeSelector, { type DateRangeValue } from './DateRangeSelector';
import MetricCard from './MetricCard';
import MetricsChart from './MetricsChart';
import { useOverviewMetrics } from './useOverviewMetrics';
import { computePresetRange } from '../../../utils/dateRange';
import { formatCurrencyExact, formatImpressions, formatPercent } from './metricsFormat';
import type { StationScopeRequest } from '../admin/stationScopeRequest';

interface MetricsSectionProps {
  /**
   * CTV-759: the Station scope discriminator, not a slug. The server
   * resolves `all` from the caller's own claims — the client never sends a
   * station set.
   */
  scope: StationScopeRequest;
  /**
   * CTV-808: whether the Net revenue popover may point at the Billing tab.
   * A sales_rep has no Billing tab, so their copy stops before the pointer.
   */
  showBillingHint?: boolean;
}

const initialRange = (): DateRangeValue => {
  const { start, end } = computePresetRange('30days', new Date());
  return { preset: '30days', start, end };
};

const MetricsSection: React.FC<MetricsSectionProps> = ({ scope, showBillingHint = true }) => {
  const [range, setRange] = React.useState<DateRangeValue>(initialRange);
  const [showNetInfo, setShowNetInfo] = React.useState(false);
  const { data, loading, error } = useOverviewMetrics({
    scope,
    startDate: range.start,
    endDate: range.end,
  });

  const totals = data?.totals ?? {
    grossCents: 0,
    netCents: 0,
    deliveredImpressions: 0,
    cpmCents: 0,
  };

  // "Net revenue" popover math (CTV-776 called the card "Your share"; CTV-780
  // renamed it back to Net revenue, the publisher's net; the card was "Net Revenue" before the
  // Billing tab existed, and "net" collided with the payout figure there):
  // Revenue − CTVBuyer's share = Net revenue. The "share %" is (gross − net) /
  // gross, which collapses to the submission's rev_share_percent when only one
  // is in scope and to a delivery-weighted blend across multiple. Mirrors the
  // internal Self-Serve dashboard.
  const ctvShareCents = totals.grossCents - totals.netCents;
  const ctvSharePercent = totals.grossCents > 0 ? ctvShareCents / totals.grossCents : 0;

  return (
    <section className="mb-8">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Metrics</h2>
        <DateRangeSelector value={range} onChange={setRange} />
      </div>
      {error && (
        <p className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{error}</p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
        <MetricCard
          title="Revenue"
          value={formatCurrencyExact(totals.grossCents)}
          type="revenue"
          loading={loading}
        />
        <div
          className="relative"
          onMouseEnter={() => setShowNetInfo(true)}
          onMouseLeave={() => setShowNetInfo(false)}
        >
          <MetricCard
            title="Net revenue"
            value={formatCurrencyExact(totals.netCents)}
            type="netRevenue"
            loading={loading}
          />
          {showNetInfo && (
            <div className="absolute z-20 mt-2 right-0 w-80 bg-white border border-blue-100 shadow-lg rounded-md p-4">
              <p className="text-sm font-semibold text-blue-700 mb-3">
                Net revenue details
              </p>
              {totals.grossCents > 0 ? (
                <div className="text-xs text-slate-700 space-y-1 tabular-nums">
                  <div className="flex justify-between">
                    <span>Revenue</span>
                    <span>{formatCurrencyExact(totals.grossCents)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>
                      − CTVBuyer&apos;s share ({formatPercent(ctvSharePercent)})
                    </span>
                    <span>−{formatCurrencyExact(ctvShareCents)}</span>
                  </div>
                  <div className="border-t border-slate-200 my-1" />
                  <div className="flex justify-between font-semibold">
                    <span>= Net revenue</span>
                    <span>{formatCurrencyExact(totals.netCents)}</span>
                  </div>
                  <p className="pt-2 text-[11px] text-slate-500">
                    {showBillingHint
                      ? 'Waymark, ad serving, and Stripe fees come off before each payout; see Billing.'
                      : 'Waymark, ad serving, and Stripe fees come off before each payout.'}
                  </p>
                </div>
              ) : (
                <p className="text-xs text-slate-500">
                  No revenue in this date range.
                </p>
              )}
            </div>
          )}
        </div>
        <MetricCard
          title="Impressions Delivered"
          value={formatImpressions(totals.deliveredImpressions)}
          type="impressions"
          loading={loading}
        />
        <MetricCard
          title="CPM"
          value={formatCurrencyExact(totals.cpmCents)}
          type="cpm"
          loading={loading}
        />
      </div>
      <MetricsChart data={data?.dailyChart ?? []} loading={loading} />
      {/* CTV-759: the per-station breakdown lives here rather than on the
          page, so one date selector drives the cards, the chart and the
          table. The API returns stationRows only under `{ scope: 'all' }`,
          so this is absent in single-station mode without a mode branch. */}
      {data?.stationRows && data.stationRows.length > 0 && (
        <div className="mt-6 bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Station</th>
                  <th className="text-right px-4 py-3 font-medium">Revenue</th>
                  <th className="text-right px-4 py-3 font-medium">Net revenue</th>
                  <th className="text-right px-4 py-3 font-medium">Impressions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {data.stationRows.map((r) => (
                  <tr key={r.stationId ?? r.stationName} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-slate-900">
                      {r.stationDisplayName || r.stationName}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-900">
                      {formatCurrencyExact(r.grossCents)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-900">
                      {formatCurrencyExact(r.netCents)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                      {formatImpressions(r.deliveredImpressions)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* No per-station CPM column: CPM is a RATIO and belongs to the
              headline, recomputed from the widened totals. A column of
              per-station CPMs invites averaging them, which a small
              high-CPM station would distort. */}
        </div>
      )}
    </section>
  );
};

export default MetricsSection;
